import { Alert, Box, Stack, TextField } from "@mui/material";
import { Span, type UseModalState } from "@src/lib/ui-react";
import { useEffect, useState } from "react";

import { Button } from "../../../components/Button";
import { Dialog } from "../../../components/Dialog";
import { IconButton } from "../../../components/IconButton";
import { LoadingButton } from "../../../components/LoadingButton";
import { Text } from "../../../components/Text";
import { useCreateWorkerLock } from "../api/useCreateWorkerLock";
import { LOCK_EXPIRY_DAYS, MAX_REASON_LENGTH, MIN_REASON_LENGTH } from "../constants";
import { LOCK_MODAL_STEPS, type LockModalStep } from "../types";

interface LockFlowDialogProps {
  modalState: UseModalState;
  workerId: string;
  workplaceId: string;
}

export function LockFlowDialog(props: LockFlowDialogProps) {
  const { modalState, workerId, workplaceId } = props;

  const [step, setStep] = useState<LockModalStep>(LOCK_MODAL_STEPS.confirmation);
  const [lockReason, setLockReason] = useState("");
  const [unlockCondition, setUnlockCondition] = useState("");
  const [error, setError] = useState<string | null>(null);

  const createWorkerLock = useCreateWorkerLock();

  const isFormValid =
    lockReason.length >= MIN_REASON_LENGTH && unlockCondition.length >= MIN_REASON_LENGTH;

  useEffect(() => {
    if (!modalState.modalIsOpen) {
      setStep(LOCK_MODAL_STEPS.confirmation);
      setLockReason("");
      setUnlockCondition("");
      setError(null);
    }
  }, [modalState.modalIsOpen]);

  const handleSubmit = async () => {
    setError(null);
    try {
      await createWorkerLock.mutateAsync({
        workplaceId,
        workerId,
        lockReason,
        unlockCondition,
      });
      modalState.closeModal();
    } catch {
      setError("Failed to lock this professional. Please try again.");
    }
  };

  const isConfirmationStep = step === LOCK_MODAL_STEPS.confirmation;

  return (
    <Dialog
      modalState={modalState}
      title={isConfirmationStep ? "Lock Professional's Account" : undefined}
      headerContent={
        isConfirmationStep ? undefined : (
          <Box
            sx={(theme) => ({
              display: "flex",
              alignItems: "center",
              gap: theme.spacing(2),
              paddingX: theme.spacing(5),
              paddingY: theme.spacing(4),
              borderBottom: `1px solid ${theme.palette.border?.base}`,
            })}
          >
            <IconButton
              aria-label="Back"
              iconType="arrow-left"
              size="small"
              variant="outlined"
              onClick={() => {
                setStep(LOCK_MODAL_STEPS.confirmation);
              }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Text bold>Lock Professional&apos;s Account</Text>
            </Box>
            <IconButton
              aria-label="Close dialog"
              iconType="close"
              size="small"
              variant="outlined"
              onClick={modalState.closeModal}
            />
          </Box>
        )
      }
    >
      {isConfirmationStep ? (
        <>
          <Stack gap={3}>
            <Text>Locking this professional will:</Text>
            <Stack component="ul" gap={1} sx={{ margin: 0, paddingLeft: 4 }}>
              <li>
                <Text>Prevent them from accepting new visits</Text>
              </li>
              <li>
                <Text>Prevent them from being assigned to new cases</Text>
              </li>
            </Stack>
            <Text>
              This will last for {LOCK_EXPIRY_DAYS} days. Enter lock details on the next page.
            </Text>
          </Stack>
          <Stack direction="row" justifyContent="flex-end" gap={2} sx={{ marginTop: 4 }}>
            <Button variant="outlined" onClick={modalState.closeModal}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setStep(LOCK_MODAL_STEPS.form);
              }}
            >
              Continue
            </Button>
          </Stack>
        </>
      ) : (
        <>
          <Stack gap={3}>
            <Stack gap={1}>
              <Text semibold>Reason for Locking</Text>
              <TextField
                multiline
                rows={4}
                placeholder="Describe why this professional's account should be locked..."
                value={lockReason}
                inputProps={{ maxLength: MAX_REASON_LENGTH }}
                onChange={(event) => {
                  setLockReason(event.target.value);
                }}
              />
              <Text
                variant="caption"
                sx={(theme) => ({
                  color:
                    lockReason.length < MIN_REASON_LENGTH
                      ? theme.palette.text?.secondary
                      : theme.palette.success?.main,
                  textAlign: "right",
                })}
              >
                {lockReason.length}/{MIN_REASON_LENGTH} minimum characters
              </Text>
            </Stack>

            <Stack gap={1}>
              <Text semibold>How to Unlock</Text>
              <TextField
                multiline
                rows={4}
                placeholder="Describe what needs to happen before this professional can be unlocked..."
                value={unlockCondition}
                inputProps={{ maxLength: MAX_REASON_LENGTH }}
                onChange={(event) => {
                  setUnlockCondition(event.target.value);
                }}
              />
              <Text
                variant="caption"
                sx={(theme) => ({
                  color:
                    unlockCondition.length < MIN_REASON_LENGTH
                      ? theme.palette.text?.secondary
                      : theme.palette.success?.main,
                  textAlign: "right",
                })}
              >
                {unlockCondition.length}/{MIN_REASON_LENGTH} minimum characters
              </Text>
            </Stack>

            <Alert severity="warning" sx={{ alignItems: "center" }}>
              <Span fontWeight="bold">
                These responses will be shared with the professional in the chat.
              </Span>{" "}
              Please use judgment and professionalism.
            </Alert>

            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
          <Stack direction="row" justifyContent="flex-end" gap={2} sx={{ marginTop: 4 }}>
            <Button variant="outlined" onClick={modalState.closeModal}>
              Cancel
            </Button>
            <LoadingButton
              isLoading={createWorkerLock.isLoading}
              variant="primary"
              disabled={!isFormValid}
              onClick={handleSubmit}
            >
              Lock Professional
            </LoadingButton>
          </Stack>
        </>
      )}
    </Dialog>
  );
}
